import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { RelatedToType } from '@shared/enums';
import { ApproverRequestItem } from '@shared/modules/approvals/approvals.types';
import {
  ApprovalProcess,
  ApprovalPanelItem,
  EligibleApprover,
  EligibleApproverApiItem,
  EligibleApproverGroup,
  EligibleApproversResponse,
} from './approvals.types';

@Injectable({ providedIn: 'root' })
export class ApprovalsService {
  private readonly http = inject(HttpClient);

  getEligibleApprovers(relatedToId: number, relatedToType: RelatedToType): Observable<EligibleApproversResponse> {
    const params = new HttpParams().set('related_to_id', String(relatedToId)).set('related_to_type', relatedToType);

    return this.http
      .get<{ eligible_approvers: EligibleApproverApiItem[] }>('approval_processes/eligible_approvers', { params })
      .pipe(
        map(({ eligible_approvers }) => ({
          eligible_approvers: eligible_approvers.filter(
            (item): item is { type: 'user' } & EligibleApprover => item.type === 'user',
          ),
          eligible_approver_groups: eligible_approvers.filter(
            (item): item is { type: 'group' } & EligibleApproverGroup => item.type === 'group',
          ),
        })),
      );
  }

  getApprovalById(id: number, relatedToId: number, relatedToType: RelatedToType): Observable<ApprovalProcess> {
    const params = new HttpParams().set('related_to_id', String(relatedToId)).set('related_to_type', relatedToType);
    return this.http
      .get<{ approval_process: ApprovalProcess }>(`approval_processes/${id}`, { params })
      .pipe(map(({ approval_process }) => approval_process));
  }

  getApprovals(relatedToId: number, relatedToType: RelatedToType): Observable<ApprovalProcess[]> {
    const params = new HttpParams().set('related_to_id', String(relatedToId)).set('related_to_type', relatedToType);

    return this.http
      .get<{ approval_processes: ApprovalProcess[] }>('approval_processes', { params })
      .pipe(map(({ approval_processes }) => approval_processes));
  }

  createApproval(
    name: string,
    templateId: number | null,
    relatedToId: number,
    relatedToType: RelatedToType,
    snapshot: Record<string, any> = {},
    layout: ApprovalPanelItem[] = [],
  ): Observable<ApprovalProcess> {
    return this.http
      .post<{ approval_process: ApprovalProcess }>('approval_processes', {
        approval_process: {
          related_to_type: relatedToType,
          related_to_id: relatedToId,
          name,
          snapshot,
          // a template brings its own layout and approvers, so no layout is sent with it
          ...(templateId != null
            ? { approval_process_template_id: templateId }
            : { layout_definition: { tab: layout } }),
        },
      })
      .pipe(map(({ approval_process }) => approval_process));
  }

  deleteApproval(id: number, relatedToId: number, relatedToType: RelatedToType): Observable<void> {
    const params = new HttpParams().set('related_to_id', String(relatedToId)).set('related_to_type', relatedToType);
    return this.http.delete<void>(`approval_processes/${id}`, { params });
  }

  updateSnapshot(
    approvalId: number,
    snapshot: Record<string, any>,
    relatedToId: number,
    relatedToType: RelatedToType,
  ): Observable<ApprovalProcess> {
    return this.http
      .put<{ approval_process: ApprovalProcess }>(`approval_processes/${approvalId}`, {
        approval_process: { snapshot, related_to_id: relatedToId, related_to_type: relatedToType },
      })
      .pipe(map(({ approval_process }) => approval_process));
  }

  updateName(
    approvalId: number,
    name: string,
    relatedToId: number,
    relatedToType: RelatedToType,
  ): Observable<ApprovalProcess> {
    return this.http
      .put<{ approval_process: ApprovalProcess }>(`approval_processes/${approvalId}`, {
        approval_process: { name, related_to_id: relatedToId, related_to_type: relatedToType },
      })
      .pipe(map(({ approval_process }) => approval_process));
  }

  updateLayout(
    approvalId: number,
    layout: ApprovalPanelItem[],
    relatedToId: number,
    relatedToType: RelatedToType,
  ): Observable<ApprovalProcess> {
    return this.http
      .put<{ approval_process: ApprovalProcess }>(`approval_processes/${approvalId}`, {
        approval_process: {
          layout_definition: { tab: layout },
          related_to_id: relatedToId,
          related_to_type: relatedToType,
        },
      })
      .pipe(map(({ approval_process }) => approval_process));
  }

  sendApprovalRequest(
    approvalId: number,
    approvers: ApproverRequestItem[],
    relatedToId: number,
    relatedToType: RelatedToType,
  ): Observable<ApprovalProcess> {
    return this.http
      .post<{ approval_process: ApprovalProcess }>(`approval_processes/${approvalId}/request_approval`, {
        approval_process: {
          approvers,
          related_to_id: relatedToId,
          related_to_type: relatedToType,
        },
      })
      .pipe(map(({ approval_process }) => approval_process));
  }

  approveReview(approvalRowId: number, comments: string): Observable<void> {
    return this.http.put<void>(`approvals/${approvalRowId}/approve`, { approval: { comments } });
  }

  declineReview(approvalRowId: number, comments: string): Observable<void> {
    return this.http.put<void>(`approvals/${approvalRowId}/decline`, { approval: { comments } });
  }
}
